import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  Divider
} from '@mui/material';
import { getOperatorData, getFleet } from '../api';
import RentalDetails from '../components/RentalDetails';

// Helper function to format date in (mm-dd-yyyy) format
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

const CheckoutPage = () => {
  const loc = useLocation();
  const [vehicleImage, setVehicleImage] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // State variables for query parameters
  const [vehicleName, setVehicleName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dayCnt, setDayCnt] = useState('');
  const [reservationAmount, setReservationAmount] = useState('');
  // Add new state variable for reservation hash
  const [reservationHash, setReservationHash] = useState('');
  // Add new state variables for price breakdown
  const [baseCharge, setBaseCharge] = useState(0);
  const [taxes, setTaxes] = useState(0);
  const [surcharges, setSurcharges] = useState([]);
  // Add new state variable for coupon percent
  const [couponPercent, setCouponPercent] = useState(0);
  // Add new state variable for note
  const [note, setNote] = useState('');
  // Add new state variable for quantity
  const [quantity, setQuantity] = useState(1);
  // Add new state variables for per-item pricing
  const [taxesPerReservation, setTaxesPerReservation] = useState(0);
  // Add new state variable for daily rate
  const [dailyRate, setDailyRate] = useState(0);
  // Add new state variable for operator data
  const [operatorData, setOperatorData] = useState('');  // Add new state variable for QR URL
  const [qrUrl, setQrUrl] = useState('');
  // Add new state variable for fleet data
  const [fleetData, setFleetData] = useState(null);

  // Handle screen resizing
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);  }, []);

  // Update document title with fleet account name
  useEffect(() => {
    const fleet = JSON.parse(localStorage.getItem('fleet')) || {};
    if (fleet.account_name) {
      document.title = fleet.account_name;
    } else if (fleetData && fleetData.account_name) {
      document.title = fleetData.account_name;
    } else {
      document.title = 'Roamr Reservations'; // fallback title
    }
  }, [fleetData]);

  // Fetch operator data and set vehicle image
  useEffect(() => {
    const queryParams = new URLSearchParams(loc.search);
    const operatorParam = queryParams.get('operator');
    const fleetParam = queryParams.get('fleet');
    const vehicleImageUrl = queryParams.get('vehicle_image_url');

    // Extract and set query parameter values
    setVehicleName(queryParams.get('vehicle_name') || 'Unknown Vehicle');
    setStartDate(formatDate(queryParams.get('start_date')));
    setEndDate(formatDate(queryParams.get('end_date')));
    setDayCnt(queryParams.get('dayCnt'));
    setReservationAmount(queryParams.get('reservation_amount'));
    // Extract reservation_hash from query params
    const reservationHashParam = queryParams.get('reservation_hash');
    setReservationHash(reservationHashParam || '');
    
    // Get price breakdown details
    const baseChargeParam = queryParams.get('base_charge');
    const taxesParam = queryParams.get('taxes');
    const dailyRateParam = queryParams.get('daily_rate');
    const surchargesParam = queryParams.get('surcharges');
    const couponPercentParam = queryParams.get('coupon_percent');
    const noteParam = queryParams.get('note');
    const quantityParam = queryParams.get('quantity');
    
    if (baseChargeParam) setBaseCharge(parseFloat(baseChargeParam) / 100);
    if (taxesParam) setTaxes(parseFloat(taxesParam) / 100);
    if (dailyRateParam) setDailyRate(parseInt(dailyRateParam));
    if (couponPercentParam) setCouponPercent(parseFloat(couponPercentParam));
    if (noteParam) setNote(decodeURIComponent(noteParam));
    if (quantityParam) {
      const qty = parseInt(quantityParam);
      setQuantity(qty);
      // Calculate per-item pricing if quantity > 1
      if (qty > 1 && taxesParam) {
        setTaxesPerReservation(parseFloat(taxesParam) / 100 / qty);
      }
    }
    
    // Parse surcharges if they exist
    if (surchargesParam) {
      try {
        console.log("Raw surcharges param:", surchargesParam);
        
        // Improved decoding logic - check if the string appears to be URL encoded
        let decodedParam = surchargesParam;
        
        // Only try to decode if it contains URL encoding characters
        if (surchargesParam.includes('%')) {
          try {
            decodedParam = decodeURIComponent(surchargesParam);
          } catch (e) {
            // Silently handle decoding errors, we'll use the raw parameter
            console.log("Using raw surcharge parameter (not URL encoded)");
          }
        }
        
        console.log("Processed surcharges param:", decodedParam);
        
        // Parse the JSON
        const parsedSurcharges = JSON.parse(decodedParam);
        console.log("Parsed surcharges:", parsedSurcharges);
        
        setSurcharges(parsedSurcharges);
      } catch (error) {
        console.error("Failed to parse surcharges:", error);
        setSurcharges([]);
      }
    }
    
    // Fetch operator data
    if (operatorParam) {
      getOperatorData(operatorParam).then((data) => {
        localStorage.setItem('operator', operatorParam);
        setOperatorData(data.contact_html || '');
        setQrUrl(data.qr_url || '');      });
    }

    // Fetch fleet data if not in localStorage
    const existingFleet = JSON.parse(localStorage.getItem('fleet') || 'null');
    if (!existingFleet && fleetParam) {
      getFleet(fleetParam)
        .then((fleetResponse) => {
          const fleet = fleetResponse?.fleet;
          if (fleet) {
            setFleetData(fleet);
            // Optionally store it in localStorage for future use
            localStorage.setItem('fleet', JSON.stringify(fleet));
          }
        })
        .catch((error) => {
          console.error('Failed to fetch fleet data:', error);
          // Set empty fleet data as fallback
          setFleetData({});
        });
    } else if (existingFleet) {
      setFleetData(existingFleet);
    }
      
    // Set vehicle image
    if (vehicleImageUrl) {
      setVehicleImage(vehicleImageUrl);
    }
  }, [loc.search]);

  const fleet = JSON.parse(localStorage.getItem('fleet')) || fleetData || {};

  // Calculate discount amount
  const discountAmount = couponPercent > 0 ? (baseCharge * couponPercent / 100) : 0;

  // Build priceDetails object compatible with RentalDetails component
  const priceDetails = {
    days: parseInt(dayCnt) || 0,
    daily_rate: dailyRate || (baseCharge && dayCnt ? Math.round((baseCharge / parseInt(dayCnt)) * 100) : 0), // Use passed daily_rate or calculate
    base_charge: Math.round(baseCharge * 100), // Convert to cents
    taxes: Math.round(taxes * 100), // Convert to cents
    taxes_per_reservation: quantity > 1 ? Math.round(taxesPerReservation * 100) : Math.round(taxes * 100),
    amount: reservationAmount,
    discount_amount: Math.round(discountAmount * 100), // Convert to cents
    surcharges: surcharges || []
  };

  return (
    <div>
      <AppBar position="static" style={{ backgroundColor: fleet.menu_bkg_color || '#333' }}>
        <Toolbar>
          <Box
            component="img"
            src={fleet.web_logo || 'default_logo.png'}
            alt="Menu Logo"
            sx={{
              backgroundColor: fleet.web_bkg_color || '#000',
              padding: '8px',
              borderRadius: '4px',
              height: '75px',
              objectFit: 'contain',
            }}
          />
        </Toolbar>
      </AppBar>

      <Grid>
        <Box>
          <Paper elevation={1} style={{ padding: '16px' }}>
            <Typography variant="h3">Reservation Confirmed!</Typography>
          </Paper>
        </Box>
      </Grid>

      <Grid container spacing={2} style={{ padding: '16px' }}>
        {isMobile ? (
          <>
            <Grid item xs={12}>
              <Box
                component="img"
                src={vehicleImage}
                alt="Vehicle Image"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  marginBottom: '16px',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={2} style={{ padding: '16px' }}>
                <Divider style={{ margin: '8px 0' }} />
                <RentalDetails
                  priceDetails={priceDetails}
                  selectedQuantity={quantity}
                  selectedStartDate={startDate}
                  selectedEndDate={endDate}
                  couponPercent={couponPercent}
                  isMobile={isMobile}
                  noteContent={note || null}
                  confirmationId={reservationHash}
                  vehicleName={vehicleName}
                />
                
                {/* What's Next section */}
                <Divider sx={{ my: 4 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  What's Next?
                </Typography>
                
                <Box sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography variant="body1" paragraph>
                    <strong>1. Confirmation Email:</strong> You will receive a confirmation email with your reservation details.
                  </Typography>
                  
                  <Typography variant="body1" paragraph>
                    <strong>2. Rental Pickup:</strong> Please bring your ID to our rental location on your reservation date.
                  </Typography>                  <Typography variant="body1" paragraph>
                    <strong>3. Enjoy Your Rental:</strong> Our staff will provide a brief overview of the vehicle operation before you head out.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={4}>
              <Box
                component="img"
                src={vehicleImage}
                alt="Vehicle Image"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '300px',
                  objectFit: 'contain',
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Paper elevation={2} style={{ padding: '16px' }}>
                <Typography 
                  variant="h6" 
                  sx={{ wordWrap: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: operatorData || 'Reserve Your Carts with Us!<br />Roamr Reservations<br />www.getroamr.com' }}
                />
                <Divider style={{ margin: '8px 0' }} />
                <RentalDetails
                  priceDetails={priceDetails}
                  selectedQuantity={quantity}
                  selectedStartDate={startDate}
                  selectedEndDate={endDate}
                  couponPercent={couponPercent}
                  isMobile={isMobile}
                  noteContent={note || null}
                  confirmationId={reservationHash}
                  vehicleName={vehicleName}
                />
                
                {/* What's Next section */}
                <Divider sx={{ my: 4 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  What's Next?
                </Typography>
                
                <Box sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography variant="body1" paragraph>
                    <strong>1. Confirmation Email:</strong> You will receive a confirmation email with your reservation details.
                  </Typography>
                  
                  <Typography variant="body1" paragraph>
                    <strong>2. Rental Pickup:</strong> Please bring your ID to our rental location on your reservation date.
                  </Typography>                  <Typography variant="body1" paragraph>
                    <strong>3. Enjoy Your Rental:</strong> Our staff will provide a brief overview of the vehicle operation before you head out.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Box
                component="img"
                src={qrUrl || "https://assets.getroamr.app/QR_redirector_tmp.png"}
                alt="App QR Code"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '300px',
                  objectFit: 'contain',
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};

export default CheckoutPage;
